import React, { useCallback, useEffect, useState } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';

import { getSales } from '../services/salesService';

import DataGridTable from '../components/table/DataGridTable';
import EmptyList from '../components/misc/EmptyList';
import RefreshButton from '../components/misc/RefreshButton';
import BulkRefundActions from '../components/sales/BulkRefundActions';
import EventListSearchBar from '../components/events/EventListSearchBar';

import { SALES_TABLE } from '../definitions';

import { checkSalesPermissions, getSalesPageTitle, getManagerColumns } from '../utils/salesUtils';
import { gridFilterModelToSalesTicketsQuery } from '../utils/salesTicketsGridFilters';

const SalesPage = () => {
  const translate = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [searchQ, setSearchQ] = useState('');
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: SALES_TABLE?.defaults?.pageSize || 50,
  });
  const [allSalesForRefund, setAllSalesForRefund] = useState([]);
  const [columns, setColumns] = useState(SALES_TABLE.columns);
  const { permissions } = usePermissions();

  const eventID = searchParams.get('eventID');
  const userID = searchParams.get('userID');
  const stockID = searchParams.get('stockID');

  const { isManager, isOperator, isEditor, canView } = checkSalesPermissions(permissions);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const apiFilters = gridFilterModelToSalesTicketsQuery(filterModel, 'sales');
      const result = await getSales({
        userID,
        eventID,
        stockID,
        q: searchQ,
        apiFilters,
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
      });
      const rows = Array.isArray(result) ? result : (result?.data ?? []);
      const total = Array.isArray(result) ? rows.length : (result?.total ?? 0);
      setData(rows);
      setRowCount(total);
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    userID,
    eventID,
    stockID,
    searchQ,
    filterModel,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  const fetchRefundSales = useCallback(async () => {
    if (!eventID) {
      setAllSalesForRefund([]);
      return;
    }
    try {
      const all = await getSales({
        userID,
        eventID,
        stockID,
        fetchAll: true,
      });
      setAllSalesForRefund(Array.isArray(all) ? all : (all?.data ?? []));
    } catch (error) {
      console.error('Error fetching sales for refund:', error);
      setAllSalesForRefund([]);
    }
  }, [userID, eventID, stockID]);

  useEffect(() => {
    if (!permissions) return;
    if (isManager && !eventID) {
      window.location.href = '/#/events-v2';
      return;
    }
    if (isManager) {
      setColumns(getManagerColumns(SALES_TABLE.columns));
    }
    fetchData();
  }, [searchParams, permissions, isManager, eventID, fetchData]);

  useEffect(() => {
    if (!permissions || !eventID) return;
    fetchRefundSales();
  }, [permissions, eventID, stockID, fetchRefundSales]);

  const handleFilterModelChange = (model) => {
    setFilterModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearch = (q) => {
    setSearchQ(q);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearSearch = () => {
    setSearchQ('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const CustomTableHeader = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <RefreshButton refreshData={fetchData} />
    </GridToolbarContainer>
  );

  const { title, description } = getSalesPageTitle(eventID, stockID, userID, translate);

  if (!permissions) {
    return <EmptyList mode="loading" />;
  }

  if (isOperator) {
    window.location.href = '/#/events-v2';
    return null;
  }

  if (isEditor) {
    window.location.href = '/#/news';
    return null;
  }

  if (!canView) {
    return (
      <EmptyList
        customMessage={translate('sales.no_permission', {
          _: "You don't have the right permission set to view this page. Please contact GişeKıbrıs if you believe this is an error.",
        })}
        noCreate={true}
      />
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
        <EventListSearchBar
          value={searchQ}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          disabled={isLoading}
          placeholderKey="sales.search_placeholder"
        />
        {eventID && (
          <BulkRefundActions
            eventID={eventID}
            sales={allSalesForRefund}
            onDone={() => {
              fetchData();
              fetchRefundSales();
            }}
          />
        )}
      </Paper>

      <Paper sx={{ flex: 1, p: 2, mt: 2 }}>
        <DataGridTable
          config={SALES_TABLE}
          rows={data}
          columns={columns}
          loading={isLoading}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          onColumnsVisibilityChange={(model) => {
            setColumns(
              columns.map((column) => ({
                ...column,
                hide: !model[column.field],
              })),
            );
          }}
          sx={{
            '.MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
          }}
          slots={{ toolbar: CustomTableHeader }}
        />
      </Paper>
    </Box>
  );
};

export default SalesPage;
